import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { History, RefreshCw, Pencil, Plus, Trash2, LogIn, Eye } from "lucide-react";
import { listHistorie, historieBereiche, listUsers } from "@/lib/api";
import { euro } from "@/lib/format";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { Seitenkopf } from "@/hilfe/Seitenkopf";

const AKTION = {
    aendern:  { label: "geändert",   icon: Pencil,  ton: "text-sky-700 bg-sky-50 border-sky-200" },
    anlegen:  { label: "angelegt",   icon: Plus,    ton: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    loeschen: { label: "gelöscht",   icon: Trash2,  ton: "text-rose-700 bg-rose-50 border-rose-200" },
    anmelden: { label: "angemeldet", icon: LogIn,   ton: "text-gray-600 bg-gray-50 border-gray-200" },
};

const SUCHFELDER = ["bereich", "bezeichnung", "aktion", "user_name", "notiz"];

// Zeit als „vor 3 Minuten" ist beim Nachforschen unbrauchbar — man vergleicht
// Einträge mit Kontoauszügen und Mails, und die tragen Datum und Uhrzeit.
const zeitpunkt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

// Werte lesbar machen, ohne sie zu deuten. Ein Objekt wird zu seinen Feldern
// aufgelöst statt als JSON hingeworfen; Zahlen bleiben Zahlen — sie als Euro zu
// formatieren wäre geraten, denn nicht jedes Feld ist Geld.
const wert = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-gray-300">leer</span>;
    if (typeof v === "boolean") return v ? "ja" : "nein";
    if (typeof v === "object") {
        const teile = Object.entries(v)
            .filter(([, x]) => x !== null && x !== undefined && x !== "")
            .map(([k, x]) => `${k}: ${typeof x === "object" ? JSON.stringify(x) : x}`);
        return <span className="text-[11px] leading-snug">{teile.join(" · ") || "—"}</span>;
    }
    return String(v);
};

const GELDFELDER = ["soll_total", "soll_summe", "betrag", "summe", "saldensumme", "kaltmiete",
                    "nebenkosten", "heizkosten", "pauschalmiete", "kaution"];

const Zelle = ({ feld, v }) => {
    if (typeof v === "number" && GELDFELDER.includes(feld)) return <>{euro(v)}</>;
    return wert(v);
};

const Historie = () => {
    const [eintraege, setEintraege] = useState([]);
    const [gesamt, setGesamt] = useState(0);
    const [bereiche, setBereiche] = useState({});
    const [nutzer, setNutzer] = useState([]);
    const [busy, setBusy] = useState(false);

    const [tiefe, setTiefe] = useState("feld");
    const [bereich, setBereich] = useState("alle");
    const [userId, setUserId] = useState("alle");
    const [von, setVon] = useState("");

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = { limit: 500 };
            if (tiefe !== "alle") params.tiefe = tiefe;
            if (bereich !== "alle") params.bereich = bereich;
            if (userId !== "alle") params.user_id = userId;
            if (von) params.von = von;
            const [d, b, u] = await Promise.all([
                listHistorie(params),
                historieBereiche(),
                listUsers().catch(() => []),
            ]);
            setEintraege(d.eintraege);
            setGesamt(d.gesamt);
            setBereiche(b.bereiche || {});
            setNutzer(u);
        } finally { setBusy(false); }
    }, [tiefe, bereich, userId, von]);

    useEffect(() => { load(); }, [load]);

    const { query, setQuery, filtered } = useFulltextFilter(eintraege, SUCHFELDER);

    const bereichsListe = useMemo(
        () => Object.entries(bereiche).sort(([a], [b]) => a.localeCompare(b)),
        [bereiche],
    );

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Verwaltung</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="historie-title">
                        Änderungshistorie
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Wer hat wann was geändert — und was stand vorher da. Der Vorher-Wert ist
                        der Punkt: ohne ihn weiß man nur, <i>dass</i> etwas anders ist, nicht was
                        zu korrigieren wäre.
                    </p>
                    <Seitenkopf id="seite.historie" />
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={busy} grund="Wird geladen" className="h-9">
                    <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
                </Button>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <Label className="text-[11px] text-gray-500">Tiefe</Label>
                        <SuchAuswahl wert={tiefe} onWaehlen={setTiefe}
                            optionen={[
                                { wert: "feld", text: "Mit Werten" },
                                { wert: "zugriff", text: "Nur Zugriffe" },
                                { wert: "alle", text: "Alles" },
                            ]}
                            className="w-[190px]"
                            testId="filter-tiefe"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] text-gray-500">Bereich</Label>
                        <SuchAuswahl wert={bereich} onWaehlen={setBereich}
                            optionen={[
                                { wert: "alle", text: "Alle Bereiche" },
                                ...bereichsListe.map(([b, z]) => ({ wert: b, text: `${b} (${z.feld + z.zugriff})` })),
                            ]}
                            className="w-[220px]"
                            testId="filter-bereich"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] text-gray-500">Zugang</Label>
                        <SuchAuswahl wert={userId} onWaehlen={setUserId}
                            optionen={[
                                { wert: "alle", text: "Alle" },
                                ...nutzer.map((u) => ({ wert: u.id, text: u.name || u.email })),
                            ]}
                            className="w-[200px]"
                            testId="filter-nutzer"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] text-gray-500">Ab</Label>
                        <Input type="date" value={von} onChange={(e) => setVon(e.target.value)}
                               className="w-[150px]" data-testid="filter-von" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-[11px] text-gray-500">Suche</Label>
                        <SearchBox value={query} onChange={setQuery} testid="historie-suche"
                                   placeholder="Bereich, Bezeichnung, Person …" />
                    </div>
                    <MatchCount matches={filtered.length} total={gesamt} label="Einträge" />
                </div>

                {tiefe === "zugriff" && (
                    <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
                        <Eye size={13} className="mt-0.5 shrink-0" />
                        <span>
                            Zugriffe halten fest, <b>dass</b> jemand etwas geändert hat, ohne die
                            Werte. Sie sind die Rückfallebene für Schreibpfade ohne Feld-Protokoll —
                            damit die Antwort auf „wer hat das angefasst" nie <i>nichts</i> ist.
                        </span>
                    </div>
                )}
            </div>

            <div className="space-y-2">
                {filtered.map((e) => {
                    const a = AKTION[e.aktion] || {
                        label: e.aktion, icon: Pencil,
                        ton: "text-gray-600 bg-gray-50 border-gray-200",
                    };
                    const Icon = a.icon;
                    return (
                        <div key={e.id} className="bg-white border border-gray-200 rounded-md p-4"
                             data-testid={`eintrag-${e.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="flex items-start gap-3 min-w-0">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium shrink-0 ${a.ton}`}>
                                        <Icon size={11} /> {a.label}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-900 truncate">
                                            {e.bezeichnung || e.bereich}
                                        </div>
                                        <div className="text-[11px] text-gray-400">
                                            {e.bereich}
                                            {e.tiefe === "zugriff" && ` · ${e.aktion}`}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-xs font-medium text-slate-600">
                                        {e.user_name || <span className="text-rose-600">unbekannt</span>}
                                    </div>
                                    <div className="text-[11px] text-gray-400 tabular-nums">
                                        {zeitpunkt(e.zeit)}
                                    </div>
                                </div>
                            </div>

                            {e.aenderungen?.length > 0 && (
                                <div className="mt-3 overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="text-[10px] uppercase tracking-wider text-gray-400">
                                            <tr>
                                                <th className="text-left font-medium pb-1 w-1/4">Feld</th>
                                                <th className="text-left font-medium pb-1">vorher</th>
                                                <th className="text-left font-medium pb-1">nachher</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {e.aenderungen.map((f, i) => (
                                            <tr key={i} className="border-t border-gray-50">
                                                <td className="py-1 pr-3 text-gray-500 align-top">{f.feld}</td>
                                                <td className="py-1 pr-3 text-gray-500 align-top">
                                                    <Zelle feld={f.feld} v={f.alt} />
                                                </td>
                                                <td className="py-1 text-slate-900 align-top">
                                                    <Zelle feld={f.feld} v={f.neu} />
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {e.notiz && (
                                <div className="mt-2 text-[11px] text-gray-500 italic">{e.notiz}</div>
                            )}
                        </div>
                    );
                })}

                {filtered.length === 0 && !busy && (
                    <div className="bg-white border border-gray-200 rounded-md px-4 py-12 text-center text-sm text-gray-400">
                        Keine Einträge für diese Auswahl.
                    </div>
                )}
            </div>

            <div className="text-xs text-gray-400 leading-relaxed max-w-3xl">
                Die Historie beginnt am 24.08.2026 — Änderungen davor sind nicht erfasst und lassen
                sich nicht nachtragen. Passwort-Hashes werden nie protokolliert, und der
                Versionszähler der Datensätze ebenso nicht: er ändert sich bei jedem Schreiben und
                würde jeden Eintrag mit einer Zeile füllen, die nichts erklärt.
            </div>
        </div>
    );
};

export default Historie;
